// GathrXR documentation — a single-page, sidebar-navigated guide to every
// feature. Content is data-light JSX so it stays easy to edit in place.

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Rocket, Users, Hammer, Sparkles, Globe2, Gauge,
  Crown, LifeBuoy, Menu as MenuIcon, Gamepad2, Wand2, Code2, Paintbrush, UserRound, Music,
} from 'lucide-react';
import './LandingPage.css';
import './DocsPage.css';

interface Section { id: string; title: string; icon: React.ReactNode; }

const SECTIONS: Section[] = [
  { id: 'start', title: 'Getting started', icon: <Rocket size={16} /> },
  { id: 'avatars', title: 'Avatars, emotes & sitting', icon: <Users size={16} /> },
  { id: 'play', title: 'Exploring: desktop, phone & VR', icon: <Globe2 size={16} /> },
  { id: 'games', title: 'Games & leaderboards', icon: <Gamepad2 size={16} /> },
  { id: 'music', title: 'Music & speakers', icon: <Music size={16} /> },
  { id: 'build', title: 'Building worlds', icon: <Hammer size={16} /> },
  { id: 'design', title: 'Interior design (AI walls)', icon: <Paintbrush size={16} /> },
  { id: 'npcs', title: 'NPCs: characters that talk', icon: <UserRound size={16} /> },
  { id: 'studio', title: 'Studio', icon: <Wand2 size={16} /> },
  { id: 'ai', title: 'AI creation tools', icon: <Sparkles size={16} /> },
  { id: 'social', title: 'Multiplayer & social', icon: <Users size={16} /> },
  { id: 'manage', title: 'Managing your worlds', icon: <Gauge size={16} /> },
  { id: 'membership', title: 'Plans, credits & Council', icon: <Crown size={16} /> },
  { id: 'developers', title: 'Developers & AI assistants', icon: <Code2 size={16} /> },
  { id: 'support', title: 'Help & support', icon: <LifeBuoy size={16} /> },
];

const Key: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="docs-key">{children}</kbd>
);

/** A screenshot from public/docs/. */
const Shot: React.FC<{ src: string; alt: string; caption?: string; phone?: boolean }> = ({ src, alt, caption, phone }) => (
  <figure className={`docs-shot ${phone ? 'docs-shot--phone' : ''}`}>
    <img src={`/docs/${src}.webp`} alt={alt} loading="lazy" decoding="async" width={phone ? 600 : 1600} height={phone ? 1298 : 900} />
    {caption && <figcaption>{caption}</figcaption>}
  </figure>
);

const DocsPage: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState('start');
  const [navOpen, setNavOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll-spy: highlight the section currently in view.
  useEffect(() => {
    const ids = SECTIONS.map((s) => s.id);
    const onScroll = () => {
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id: string) => {
    setNavOpen(false);
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 90, behavior: 'smooth' });
  };

  return (
    <div className="landing-container docs-root" style={{ minHeight: '100dvh' }}>
      <div className="landing-glow" />

      <nav className="landing-nav docs-topnav">
        <div className="nav-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          Gathr<span className="nav-logo-accent">XR</span> <span className="docs-topnav-tag">Docs</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="nav-btn docs-navtoggle" onClick={() => setNavOpen((v) => !v)}>
            <MenuIcon size={15} /> Contents
          </button>
          <button className="nav-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={15} /> Dashboard
          </button>
        </div>
      </nav>

      <div className="docs-layout">
        {/* Sidebar */}
        <aside className={`docs-sidebar ${navOpen ? 'open' : ''}`}>
          <div className="docs-sidebar-inner">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                className={`docs-navitem ${active === s.id ? 'active' : ''}`}
                onClick={() => go(s.id)}
              >
                {s.icon}<span>{s.title}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Content */}
        <main className="docs-content" ref={contentRef}>
          <header className="docs-hero">
            <h1>GathrXR Documentation</h1>
            <p>
              Everything you can do in GathrXR — making your avatar, exploring on
              a headset, laptop or phone, building worlds, generating them with
              Studio, and playing games together. New here? Start at the top.
            </p>
          </header>

          {/* ---------- Getting started ---------- */}
          <section id="start" className="docs-section">
            <h2><Rocket size={20} /> Getting started</h2>
            <p>
              GathrXR runs entirely in your browser — no download — on desktop,
              on phones and tablets, and in VR headsets like the Meta Quest.
            </p>
            <ol className="docs-steps">
              <li><strong>Create an account.</strong> Click <em>Start free</em> (or <em>Sign in</em>) on the home page and sign up with your email.</li>
              <li><strong>Get your free avatar.</strong> New accounts pick a <strong>free robot avatar</strong> on the Welcome screen — it's ready instantly, so you can play right away (one per account; confirm your email first if asked) — or make one in the <strong>free Avatar Builder</strong> (beta, anime style). Custom 3D avatars (including the 16 starter looks) need the Starter plan or a code. You can make more any time from <em>Avatars</em> (see the next section). This is the character other people see.</li>
              <li><strong>Enter a world.</strong> Pick a featured world on the home page, or from the dashboard hover a world card and click <em>Enter</em> to explore, or <em>Build</em> (on your own worlds) to edit.</li>
              <li><strong>Create your own world.</strong> Type a name in the <em>Name your world…</em> box on the home page and click <em>Create world</em>, or click <em>Create World</em> in the dashboard's side menu. You can start <strong>blank</strong>, from a 3D environment you <strong>upload</strong>, or from a <strong>copy of one of your worlds</strong>.</li>
            </ol>
            <Shot src="dashboard" alt="The GathrXR dashboard showing a grid of worlds" caption="Your dashboard: hover a world to Play or Build it." />
            <Shot src="new-world" alt="The New world dialog with Blank, Upload environment and copies of existing worlds" caption="Create World: start blank, upload an environment, or copy one of your worlds." />
            <div className="docs-callout">
              <strong>Two ways into a world:</strong> <em>Enter</em> drops you into <strong>play mode</strong> (walk around as your avatar, no editing tools). <em>Build</em> opens the <strong>editor</strong> with all the creation tools. You can always switch.
            </div>
            <h3>Have a code?</h3>
            <p>
              Invite and beta-tester codes add avatar builds and credits to your account. Open the link you were
              given, or go to <a onClick={() => navigate('/redeem')} className="docs-inline-link">gathrxr.com/redeem</a> (also
              under <em>Redeem a code</em> in the dashboard's account menu) and paste the code. Not signed up yet? You'll be
              asked to make an account first and the code is applied straight after. One code per account.
            </p>
            <Shot src="redeem" alt="The Have a code? page with a code box and Apply button" />
          </section>

          {/* ---------- Avatars ---------- */}
          <section id="avatars" className="docs-section">
            <h2><Users size={20} /> Avatars, emotes & sitting</h2>

            <h3>Creating your avatar</h3>
            <p>
              The avatar creator runs on the Welcome screen for new accounts and at <em>Avatars → New avatar</em>.
            </p>
            <div className="docs-callout">
              <strong>Free robot avatar:</strong> every new account can pick one free robot. It's a ready-made
              avatar, so it's equipped instantly — no build, no code. Custom looks (your own description or photo),
              Outfit Studio and Recolor are on the Starter plan or a code.
            </div>
            <div className="docs-callout">
              <strong>Free Avatar Builder (beta):</strong> for every account, on any plan. Pick an anime-style body, skin,
              hair, eyes, face and outfit, or let AI fill the parts in from a description or a selfie (your photo isn't
              saved). It's put together in your browser, so it doesn't use your avatar builds. Open it from the
              <em> New avatar</em> screen or <em>Avatars → Build free</em>; it saves to your wardrobe as a VRM avatar.
            </div>
            <p>A custom look takes a few minutes to build, and you can leave the page while it builds.</p>
            <ol className="docs-steps">
              <li><strong>Start from a look.</strong> Pick one of 16 starter looks — seven realistic people and nine styles (cyberpunk, anime, streetwear, fantasy and more) — or describe the look you want.</li>
              <li><strong>Make it look like you</strong> (optional). Upload a clear, full-body photo and GathrXR redraws you in that look.</li>
              <li><strong>Build.</strong> GathrXR turns the picture into a full 3D character and rigs it, so it walks, runs, jumps, dances and does every emote straight away — no extra steps.</li>
            </ol>
            <div className="docs-callout">
              Tips for the best result: one person, head to toe in frame, plain background, even lighting, arms a little away from the body.
            </div>
            <Shot src="avatar-create" alt="The avatar creator with starter looks and style options" caption="Pick a starter look, or design one with style, body, hair and outfit options." />

            <h3>Your wardrobe</h3>
            <p>
              <em>Avatars</em> keeps every avatar you've made. <em>Equip</em> one to use it in every world, and use the
              trash-can button on a card to delete one you don't want (if it's the one you're wearing, you switch to your next newest).
            </p>

            <h3>Bring your own VRM avatar</h3>
            <p>
              Already have a character from <strong>VRoid Studio</strong>, VRoid Hub or any UniVRM exporter? Upload the
              <code>.vrm</code> file (VRM 0.x or 1.0, up to 30 MB) at <em>Avatars → Upload VRM</em>, or from the
              <em> New avatar</em> screen. It's free — nothing is built, so it's ready right away.
            </p>
            <ul className="docs-list">
              <li><strong>Moves like every avatar:</strong> walks, runs, jumps, sits, dances and plays every emote — GathrXR fits its animations to the VRM's humanoid skeleton.</li>
              <li><strong>Comes alive:</strong> hair, skirts and accessories swing with the VRM's spring bones, the avatar blinks on its own, and its mouth moves when you talk on voice chat. Other people see all of it.</li>
              <li><strong>First person & VR:</strong> your own head is hidden so it never blocks the view; the rest of your body stays visible when you look down.</li>
              <li><strong>Size:</strong> the avatar keeps its real height, and your name tag and first-person eye height follow it.</li>
            </ul>
            <div className="docs-callout">
              <strong>Licenses are honoured.</strong> GathrXR reads the license inside the VRM and shows it before you upload.
              If it only allows the author (or people with a separate license) to use it as an avatar, you must confirm that's you.
              Avatars whose license forbids redistribution can't be sold or gifted, and non-commercial ones can't be sold.
              Only upload avatars you made or are allowed to use.
            </div>
            <div className="docs-callout">
              <strong>Quest tips:</strong> under ~70k triangles and fewer materials runs best. Big textures are scaled down to 1024 px on
              Quest automatically and outlines are skipped there. The upload screen warns you about anything heavy.
              The file must contain a full humanoid skeleton (hips, spine, head, arms, legs); VRoid exports always do.
            </div>

            <h3>Emotes & dances</h3>
            <p>
              Every world has an <strong>emote wheel</strong> — the smiley button at the bottom of the screen.
              <em> My motions</em> holds the favourites; the other tabs hold the full library — dances, emotions,
              performances, sports and everyday actions.
            </p>
            <ul className="docs-list">
              <li><strong>Gestures</strong> (play once): 👋 Wave, 🙌 Cheer, 🙇 Bow, ❤️ Heart, 🤷 Shrug, ✊ Fist Pump, 👏 Clap, 😄 Laugh, 👉 Point, 😘 Blow a Kiss, ✋ High Five, 🤗 Hug.</li>
              <li><strong>Dances</strong> (loop until you move): 🕺 Funny Dance, 🎵 Gangnam, 💃 Hip Hop, ✨ Shake It, 💃 Salsa, 🤖 Robot Dance, 🍑 Twerk.</li>
            </ul>
            <p className="docs-keys-row">
              Hotkeys: <Key>1</Key>–<Key>0</Key> fire your first ten motions · <Key>B</Key> toggles the wheel · <em>Stop motion</em> or start walking to end a dance.
            </p>
            <Shot src="emote-wheel" alt="The emote panel open over a nightclub dance floor" caption="My motions: tap one, or press its number key." />

            <h3>Sitting</h3>
            <p>
              Walk up to a chair, couch or seat and sit — or choose a sit style in the wheel: 🪑 <em>Chair</em>,
              🧘 <em>Floor</em> or 🧎 <em>Squat</em>. Everyone in the world sees your emotes, dances and seat in real time.
            </p>
          </section>

          {/* ---------- Play ---------- */}
          <section id="play" className="docs-section">
            <h2><Globe2 size={20} /> Exploring & playing</h2>

            <h3>Moving around (desktop)</h3>
            <p>When you <em>Enter</em> a world with an avatar, you play in third person — you see yourself from behind.</p>
            <p className="docs-keys-row">
              <Key>W</Key><Key>A</Key><Key>S</Key><Key>D</Key> move · <Key>Shift</Key> run · <strong>drag mouse</strong> to orbit the camera · <strong>scroll</strong> to zoom · <strong>click the ground</strong> to walk there · <strong>click a seat</strong> to sit · <Key>Space</Key> jump.
            </p>
            <Shot src="play-third-person" alt="An avatar standing in front of the stage in Club Cheetah" caption="Play mode in Club Cheetah." />

            <h3>Phone & tablet</h3>
            <p>
              Open any world link in your phone's browser. Drag the on-screen <strong>joystick</strong> to walk,
              drag anywhere else to look around, and use the bottom bar for emotes, chat, voice and the menu.
            </p>
            <Shot src="phone-play" alt="GathrXR on a phone with a joystick and a bottom bar" phone />

            <h3>Camera modes</h3>
            <p>The <em>Normal / Zoom / First-person</em> toggle at the bottom switches how you view the world — third-person, closer in, or through your own eyes.</p>

            <h3>Screenshots & recording</h3>
            <p>Use the <em>Screenshot</em> and <em>Record</em> buttons (bottom-left) to capture stills or video of your session.</p>

            <h3>Portals</h3>
            <p>
              Step onto a portal's glowing ring to travel: to another spot in the same world, or straight into
              another world. Its label tells you where it goes. When you arrive in another world you appear beside
              its return portal, if it has one.
            </p>

            <h3>VR mode</h3>
            <p>
              On a Meta Quest or other WebXR headset, open a world in the headset's browser and press <em>Enter VR</em>.
              Walk and teleport with the controllers — or put them down and use your <strong>hands</strong>: hand
              tracking is built in, and a pinch works like a click. The wrist menu lets you unmute voice, emote,
              take <strong>selfies</strong> and photos, and more.
            </p>
            <div className="docs-callout">
              No avatar yet? You'll enter in <strong>view-only</strong> first-person — you can still explore, chat, and use voice; you just won't have a visible body until you make an avatar.
            </div>
          </section>

          {/* ---------- Games ---------- */}
          <section id="games" className="docs-section">
            <h2><Gamepad2 size={20} /> Games & leaderboards</h2>
            <p>Any world can host game night. A game is a table you place in the world; people walk up, take a seat and play together.</p>
            <ul className="docs-list">
              <li><strong>Card & table games</strong>: Spades, Tonk, Dominoes, Chess and Checkers.</li>
              <li><strong>Trivia Night</strong> — a host runs the questions and everyone buzzes in; made for 2 to 20 people in a club or hall. Over 1,000 questions.</li>
              <li><strong>Drawing Guess</strong> — Pictionary-style: one person draws (in VR you draw in the air) and everyone else guesses.</li>
              <li><strong>Spin the Bottle / Truth or Dare</strong> — a party game with PG truths and dares.</li>
            </ul>
            <h3>Playing</h3>
            <ol className="docs-steps">
              <li>Walk up to a game table and <strong>take a seat</strong>. Short a player? Some games let you seat a computer player.</li>
              <li>When enough people are seated, press <em>Start game</em>. Some games let friends join part-way through.</li>
              <li>Every game keeps a <strong>leaderboard</strong>. See the <em>Games</em> tab on your dashboard.</li>
            </ol>
            <Shot src="game-spades" alt="A Spades game in progress with computer players and a hand of cards" caption="Spades against three computer players: bid, then play your cards from the panel." />
            <h3>Arcade cabinets</h3>
            <p>Four single-player arcade games come in their own upright cabinets: <strong>Stack Drop</strong> (falling blocks), <strong>Gem Swap</strong> (match three), <strong>Bubble Pop</strong> (bubble shooter) and <strong>Bug Blaster</strong> (shoot the centipede).</p>
            <ul className="docs-list">
              <li>Walk up to a cabinet and face it: its panel opens with the high scores and how to play. Press <em>Play</em> (or Enter) and the game opens over the world.</li>
              <li><strong>Desktop</strong>: arrows or WASD to move, Space or Enter for the main button, Shift or X for the second. P pauses, Esc closes. Game controllers work too.</li>
              <li><strong>Phone and tablet</strong>: an on-screen pad with A and B buttons, or tap and drag right on the game.</li>
              <li><strong>VR</strong>: poke Play, or point at it and pull the trigger. Thumbstick moves, trigger is A, grip is B, or poke the pad under the game. Your thumbstick doesn&rsquo;t move or turn you while you play; press Exit or walk away to stop.</li>
              <li>High scores are shared: finish a game while signed in and your best goes on this world&rsquo;s board (the cabinet panel shows the top 3). Your own best is also kept on your device. Other people see the cabinet&rsquo;s demo screen and a note when someone is playing it.</li>
            </ul>
            <h3>Leaderboards</h3>
            <p>Put a scoreboard in your arcade: <em>Games → Arcade → Leaderboard</em>, then click where it goes. Select it to choose the game (or <em>All games</em>, which rotates), the period (all time, this week, today), whose scores (this world or all worlds), the title, the colours, top 5 or top 10, the size, and whether it stands on the floor or hangs on a wall. Boards update on their own while people play.</p>
            <h3>Adding a game to your world</h3>
            <p>In the builder, open the <em>Games</em> menu (next to <em>Add</em>). Pick a table game or an arcade cabinet, then click where it should go. Move or rotate it like any other object; select a cabinet to switch which game it plays. In VR, use the build palette&rsquo;s <em>Games</em> tile.</p>
          </section>

          {/* ---------- Music ---------- */}
          <section id="music" className="docs-section">
            <h2><Music size={20} /> Music &amp; speakers</h2>
            <p>Give your world a soundtrack, or put speakers around it. Everyone in the world hears the same moment of the song, on desktop, phone and in VR, and people who arrive late join mid-track.</p>
            <h3>World music</h3>
            <ol className="docs-steps">
              <li>In the builder, open <em>Add → Audio → World music</em> (or <em>World → Music</em>).</li>
              <li>Add tracks from the built-in library (lounge, ambient, upbeat, chill, cinematic and retro arcade; all free CC0 music) or upload your own.</li>
              <li>Set repeat (whole playlist, one track, or stop at the end), shuffle, the volume, and whether it plays for visitors as they arrive. Press play: it starts for everyone.</li>
            </ol>
            <h3>Speakers</h3>
            <p><em>Add → Audio → Speaker</em>, then click where it goes. Select it to pick its tracks, volume and <strong>range</strong>: it&rsquo;s loudest up close and silent past its range, so a lounge corner and a dance floor can play different music. In VR it&rsquo;s in the build palette next to the games.</p>
            <h3>Uploading music</h3>
            <p>MP3, OGG or M4A, up to 20 MB each. You must confirm you own the rights to the audio, or that it&rsquo;s royalty-free or licensed for this use; we record that with the track. Copyrighted music without permission is removed.</p>
            <h3>Listening</h3>
            <ul className="docs-list">
              <li>The <strong>music button</strong> (bottom left; on phones under <em>More</em>; in VR in your wrist menu) shows what&rsquo;s playing and has your own <strong>volume and mute</strong>, saved on your device.</li>
              <li>Music gets quieter while anyone is talking in voice chat, then comes back.</li>
              <li>Browsers start sound only after you tap or click. If you see <em>Tap for music</em>, tap it (or anywhere).</li>
              <li>The world&rsquo;s owner and builders control play, pause, next and previous for everyone. Visitors can&rsquo;t change what&rsquo;s playing.</li>
              <li>Hear something that shouldn&rsquo;t be there? Use <em>Report music</em> in the music panel.</li>
            </ul>
          </section>

          {/* ---------- Build ---------- */}
          <section id="build" className="docs-section">
            <h2><Hammer size={20} /> Building worlds</h2>
            <p>
              Open a world with <em>Build</em> to get the editor. Along the top are four menus:
              <strong> Add</strong> (place things), <strong>Games</strong> (game tables and arcade cabinets), <strong>View</strong> (open panels &amp; tools),
              and <strong>World</strong> (sharing &amp; multiplayer settings).
            </p>

            <h3>Video: the build toolbar in 90 seconds</h3>
            <video className="docs-video" controls preload="metadata" src="/videos/toolbar-tutorial.mp4">
              Your browser does not support embedded video.
            </video>
            <p>A narrated walkthrough of every button on the builder toolbar, left to right.</p>

            <h3>The Add menu</h3>
            <ul className="docs-list">
              <li><strong>Asset Library</strong>: the GathrXR library (doors, venues and other ready-made pieces) and a search of thousands of free models. Click one, then click where it goes.</li>
              <li><strong>AI Object</strong> and <strong>Studio</strong>: generate a single model or a whole world (see Studio and AI creation tools below).</li>
              <li><strong>Marble World</strong> (coming soon): AI-generated walk-in worlds from a prompt or a photo. It shows a <em>Soon</em> badge for now; meanwhile, use Studio or Import 3D Environment.</li>
              <li><strong>Import 3D Environment</strong>: upload your own venue or level (.glb).</li>
              <li><strong>Skyboxes</strong>: generate a sky, pick one, or upload your own.</li>
              <li><strong>My Uploads</strong>: your own images, audio and 3D models.</li>
              <li><strong>Browser Panel</strong>: an in-world screen showing a web page or video.</li>
              <li><strong>NPC</strong>: a character that lives in your world (see NPCs below).</li>
              <li><strong>Design a wall (AI)</strong>: see Interior design below.</li>
              <li><strong>Portal entrance / Portal exit</strong>: see Portals below.</li>
              <li><strong>Spawn Point</strong>: where players appear when they enter.</li>
            </ul>
            <p><em>+ Add Shape</em> on the bottom toolbar places a Box, Sphere, Cylinder, Cone, Plane, Torus or 3D Text, and <em>Light</em> adds a light.</p>
            <Shot src="asset-library" alt="The Asset Library with the GathrXR door collection and venues" caption="Asset Library: the GathrXR doors and venues, plus free-model search." />

            <h3>Moving, rotating & scaling</h3>
            <p>Click any object to select it, then drag the coloured arrows, or use the panel on the right for exact numbers, colour, glass, glow and materials.</p>
            <p className="docs-keys-row">
              <Key>Q</Key> move · <Key>E</Key> rotate · <Key>R</Key> scale · <Key>Esc</Key> deselect · <Key>Ctrl/⌘</Key>+<Key>Z</Key> undo · <Key>Ctrl/⌘</Key>+<Key>Shift</Key>+<Key>Z</Key> redo.
            </p>
            <Shot src="build-move" alt="A selected box with move arrows and the Box Properties panel" />
            <div className="docs-callout">
              <strong>Grid snap</strong> is on by default (the <em>0.5</em> on the toolbar): things move in half-metre steps, so a small
              drag may not move at all. Click the magnet to turn snapping off for fine moves, or click <em>0.5</em> to switch to 0.25 or 1.
            </div>

            <h3>Build tools (bottom toolbar)</h3>
            <ul className="docs-list">
              <li><strong>Top-down view</strong> (<Key>T</Key>): look straight down, with roofs cut away so you can place things inside.</li>
              <li><strong>Marquee select</strong>: drag a box to select several things, drag inside it to move them, <Key>G</Key> groups them.</li>
              <li><strong>Box tool</strong>: drag a footprint on the ground, then pull it up to set the height.</li>
              <li><strong>Wall</strong> and <strong>Room</strong>: draw walls, or drag a rectangle for four walls and a floor.</li>
              <li><strong>Doorway, Window, Arch, Wide arch</strong>: hover a wall and click to cut an opening.</li>
              <li><strong>Stairs</strong>: drag on the ground for a run of steps.</li>
              <li><strong>Foliage brush</strong>: paint plants and trees onto the ground.</li>
              <li><strong>Paint</strong>: click shapes to recolour them.</li>
              <li><strong>Screenshot</strong> and <strong>Record</strong>: capture the build view.</li>
            </ul>
            <Shot src="room-tool" alt="A room made with the Room tool: four walls and a floor" caption="One drag with the Room tool." />

            <h3>Locking objects</h3>
            <p>
              Lock anything you don't want moved by accident: the lock button on its properties panel, or the lock
              icon on its row in <em>View → Scene Objects</em>. A locked object can't be selected by clicking. Click it and
              it flashes red, Scene Objects opens on its row, and you unlock it there. Locks are saved with the world
              and apply to everyone who builds in it.
            </p>
            <Shot src="locked-object" alt="A locked box flashing red with the Scene Objects list open" caption="Clicking a locked object: it flashes red and Scene Objects opens so you can unlock it." />

            <h3>Portals</h3>
            <p>
              <em>Add → Portal entrance</em>, click where it goes, then <em>Portal exit</em> and click again. Players who step
              on the entrance come out at the exit. To link to <strong>another world</strong> instead, select the entrance and
              choose <em>Another world</em> in its panel, then pick the world. Add a label so people know where it leads.
              Only people who can open that world can travel there.
            </p>
            <Shot src="portal-another-world" alt="The Portal Properties panel with Another world selected and a list of worlds" caption="A portal leading to another world." />

            <h3>The View menu</h3>
            <ul className="docs-list">
              <li><strong>Scene Objects</strong>: everything in your world. Select, hide, lock or delete items.</li>
              <li><strong>Ground</strong>: resize the floor, pick a colour, and apply materials (realistic PBR textures).</li>
              <li><strong>Atmosphere</strong>: fog, particles, bloom and lighting.</li>
              <li><strong>Object Controls</strong>: the properties panel for whatever is selected.</li>
            </ul>
            <Shot src="sky-panel" alt="The Sky panel with Muse, HQ 360 HDR and Skybox AI options" caption="Skyboxes: generate a sky, use one in this world, or upload your own." />

            <h3>Colliders (making things solid)</h3>
            <p>
              Any shape or model can be made <strong>solid</strong> so players collide with it instead of
              walking through. You can also make an object a <strong>collider only</strong> (invisible but solid),
              which is how invisible walls and world boundaries are built.
            </p>
            <h3>Building in VR</h3>
            <p>
              In a headset, open the build menu from your wrist to browse the library, place things, and grab,
              move, turn and scale them with your hands or controllers.
            </p>
            <div className="docs-callout">Click <em>Save</em> often. Your world, its objects, skybox, ground, spawn point, and settings all save together.</div>
          </section>

          {/* ---------- Interior design ---------- */}
          <section id="design" className="docs-section">
            <h2><Paintbrush size={20} /> Interior design (AI walls)</h2>
            <p>
              Give any wall a new look in a few clicks. The AI looks at the whole room (colours, furniture, light)
              and designs three options that fit it: murals, wallpapers, panelling, even built-in shelves or arches.
            </p>
            <ol className="docs-steps">
              <li>In the editor, open <em>Add → Design a wall (AI)</em>.</li>
              <li><strong>Drag a box</strong> over the wall you want to change.</li>
              <li>Optionally type a direction ("lush botanical mural", "moody dark panelling") or tap an idea. Leave it blank and the AI decides.</li>
              <li>Click <em>Design 3 options</em>. It takes about a minute.</li>
              <li>Click an option to put it on the wall, try the others, and click <em>Done</em>. <Key>Ctrl/⌘</Key>+<Key>Z</Key> undoes it.</li>
            </ol>
            <div className="docs-shots">
              <Shot src="design-wall-options" alt="Three AI wall designs: a landscape mural, arched niches with shelving, fluted panelling" caption="Three designs made for this room." />
              <Shot src="design-wall-applied" alt="The wall with arched niches and shelving applied" caption="Arched niches with shelving, applied." />
            </div>
            <div className="docs-callout">
              Works on walls built from shapes (boxes, planes, the Room and Wall tools) and on parts of imported models.
              Each design uses your AI allowance.
            </div>
          </section>

          {/* ---------- NPCs ---------- */}
          <section id="npcs" className="docs-section">
            <h2><UserRound size={20} /> NPCs: characters that talk</h2>
            <p>
              NPCs are characters who live in your world: a club manager at the bar, a host by the door, dancers on
              the floor. Add one with <em>Add → NPC</em>.
            </p>
            <ol className="docs-steps">
              <li><strong>Look</strong>: choose gender, background, type and style, add details, and preview the look. Or use one of your own avatars.</li>
              <li><strong>Animation</strong>: standing, relaxed, walking around, dancing, waving and more.</li>
              <li><strong>Advanced</strong>: turn on <em>Talks with visitors</em>, give it a personality, a greeting and a voice.</li>
              <li>Click <em>Build &amp; place</em>. Building takes a few minutes; keep working meanwhile.</li>
            </ol>
            <Shot src="npc-creator" alt="The Add an NPC dialog with look options" />
            <h3>Talking to an NPC</h3>
            <p>
              Walk up to an NPC that talks and press <Key>T</Key> (or <em>Talk to …</em>). Type, or hold the mic and speak.
              It answers out loud, and you hear it louder the closer you stand. In VR a <em>Talk</em> button floats by the NPC.
            </p>
            <Shot src="npc-talk" alt="Chatting with Nia, an NPC club manager" caption="Asking Nia, the club manager, what's on tonight." />
            <div className="docs-callout">
              The world's owner pays for NPC replies with talk credits. When they run out, the NPC says it's resting.
            </div>
          </section>

          {/* ---------- Studio ---------- */}
          <section id="studio" className="docs-section">
            <h2><Wand2 size={20} /> Studio</h2>
            <p>
              <a onClick={() => navigate('/studio')} className="docs-inline-link">Studio</a> builds a
              <strong> walk-in world</strong> (a club, a loft, a gallery…) or a single <strong>model</strong> for your
              world from a sentence — or from a reference image you upload.
            </p>
            <ol className="docs-steps">
              <li><strong>Describe it.</strong> Choose <em>World</em> or <em>Model</em>, write what you want, and pick a style, a size (worlds) and a quality.</li>
              <li><strong>Pick a reference.</strong> Studio sketches four options; choose the one closest to what you want (or regenerate). Uploading your own image skips this step.</li>
              <li><strong>Let it build.</strong> Studio builds it in 3D and checks its work against your reference. You can leave the page — it keeps going.</li>
              <li><strong>Use it.</strong> Open the finished world, or add the model to any of your worlds.</li>
            </ol>
            <Shot src="studio" alt="Studio with a world description, style, size and quality chosen" />
            <h3>From a picture</h3>
            <p>
              <em>From a picture</em> turns one photo of a room (or a description) into a world of separate objects:
              Studio finds each piece of furniture and decor, builds it as its own 3D model and puts it where it stood,
              so you can move, swap or delete any of them afterwards.
            </p>
            <h3>Studio credits</h3>
            <div className="docs-table-wrap">
              <table className="docs-table">
                <thead><tr><th></th><th>Draft</th><th>Standard</th><th>HQ</th></tr></thead>
                <tbody>
                  <tr><td>Model</td><td>1 credit · ~3–5 min</td><td>2 credits · ~4–8 min</td><td>3 credits · ~6–10 min</td></tr>
                  <tr><td>World</td><td>2 credits · ~6–10 min</td><td>3 credits · ~10–15 min</td><td>6 credits · ~20–25 min</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              Credits come with a Creator Council plan, or buy a <strong>Studio pack — 6 credits for $9.99</strong>,
              one time, right inside Studio. If a build fails, its credits come back automatically. The same credits pay
              for <em>Skybox AI</em> skies: Standard 1 credit, Enhanced 2.
            </p>
          </section>

          {/* ---------- AI ---------- */}
          <section id="ai" className="docs-section">
            <h2><Sparkles size={20} /> AI creation tools</h2>
            <p>GathrXR is AI-native — you can build almost anything by describing it.</p>

            <h3>Marble World (coming soon)</h3>
            <p>
              Marble worlds — full walk-in 3D worlds generated from a text prompt or a photo, powered by
              World Labs' Marble — are coming soon. Until then, build a world with <strong>Studio</strong> or bring your
              own with <em>Add → Import 3D Environment</em>. Worlds that already include a Marble world still load and work as before.
            </p>

            <h3>AI Object (Meshy)</h3>
            <p><em>Add → AI Object</em> turns a description into a reference picture and then a single 3D model, textured and ready to place.</p>

            <h3>AI skies</h3>
            <p>
              <em>Add → Skyboxes</em> generates a 360° sky from a prompt ("night sky with aurora", "golden hour over
              mountains"): <em>Muse</em> for a quick painted sky, <em>HQ 360 HDR</em> for a true 4K sky that also lights the
              scene, or <em>Skybox AI</em>.
            </p>
            <p>
              <strong>Muse skies are free</strong> (they use your AI allowance). <strong>Skybox AI</strong> (Blockade Labs) paints a
              true 360° panorama in the style you pick — Freeform Photoreal, Cinematic Realism, Above the Clouds, Fantasy Art,
              Deep Space, Anime, UE Render and more (<em>More styles</em> shows them all). You can add things to
              <em> leave out</em>. It costs <strong>Studio credits</strong>: <strong>Standard 1 credit</strong>,
              <strong> Enhanced 2 credits</strong> (sharper, takes a little longer). If a sky fails, its credits come back
              automatically. Out of credits? The panel offers a Studio pack (6 credits for $9.99). Skybox AI can pause near the
              end of a very busy month — Muse is always there.
            </p>

            <h3>Free model library</h3>
            <p>
              The search box in <em>Add → Asset Library</em> finds free 3D models (poly.pizza, CC0 and CC-BY);
              click one to place it. Attribution shows when you hover it.
            </p>
            <div className="docs-callout">
              These tools use your AI allowance — see <em>Plans, credits & Council</em> below. Studio and Skybox AI use Studio credits (above).
            </div>
          </section>

          {/* ---------- Social ---------- */}
          <section id="social" className="docs-section">
            <h2><Users size={20} /> Multiplayer & social</h2>
            <p>Worlds are shared spaces — anyone in the same world sees each other move, emote, and build in real time.</p>
            <ul className="docs-list">
              <li><strong>Voice chat</strong> — click <em>Join Voice</em> to talk with spatial audio (people sound closer when they're near you).</li>
              <li><strong>Text chat</strong> — the <em>Chat</em> button opens a text channel that works across desktop and VR.</li>
              <li><strong>Presence</strong> — you'll see who else is in a world (and the dashboard shows live "online now" counts on each world card).</li>
              <li><strong>Name tags</strong> — everyone's name floats above their avatar, so you always know who you're talking to.</li>
              <li><strong>Room screens</strong>: in rooms with a big screen, <em>Share my screen</em> shows a tab, window or your desktop to everyone, with sound if you share a Chrome tab. It's free for everyone, and headset users see it in VR too. <em>Share link</em> puts YouTube or a website on the screen in a shared cloud browser that anyone in the room can click and scroll; starting one is a <a href="/council">Creator Council</a> perk with a monthly allowance of minutes (every viewer's minute counts). Watching is always free, and the screen connects when you're near it. Members can also share a video file link (.mp4, .webm), which plays in sync for everyone and doesn't use the allowance.</li>
            </ul>
            <div className="docs-shots">
              <Shot src="screen-share-link" alt="The Share to Cinema screen dialog" caption="Share link (Council perk): paste YouTube, a website or a video file." />
              <Shot src="cinema-watch" alt="An avatar seated in a cinema watching a video on the big screen" caption="Everyone watches together." />
            </div>

            <h3>Go to another world, and bring people along</h3>
            <p>
              In a world, open <strong>Worlds</strong> (the globe button; on phones it's under <em>More</em>, in VR it's
              <em> Go to a world</em> in your menu). Search or browse your worlds, worlds shared with you, featured and public
              worlds, then pick one and choose <strong>Go now</strong>. To bring people with you, pick people here (or
              <em> Invite everyone here</em>) and choose <strong>Invite</strong>. They see "&lt;you&gt; invited you to &lt;world&gt;"
              and can choose <em>Go</em> or <em>Not now</em>. For a private world, the owner and its builders can invite anyone,
              and accepting gives that person visitor access. You can send up to 30 invites an hour.
            </p>

            <h3>Profiles, connections and meetups</h3>
            <p>
              Everyone has a public profile at <strong>gathrxr.com/u/&lt;username&gt;</strong> with their name, picture,
              a short bio, when they joined, how many connections they have and their <em>public</em> worlds (private worlds
              never show). Set your username, display name and bio on your <a href="/profile">Profile</a> page. In a world,
              tap someone&rsquo;s name and choose <em>Profile &amp; connect</em>.
            </p>
            <ul className="docs-list">
              <li><strong>Connect</strong>: press <em>Connect</em> on a profile. They can accept or decline; once accepted you&rsquo;re connected both ways. Manage everything from <em>Connections</em> in the dashboard account menu (your connections, requests waiting for you, requests you sent). Either of you can remove a connection. People who blocked each other can&rsquo;t connect.</li>
              <li><strong>Meetup links</strong>: on one of your world cards open <em>⋯ → Share meetup</em> (or <em>Meetup link</em> in the toolbar inside your world, or <a href="/meetups">Meetups</a>). Add a title, an optional date and time (with time zone) and a description, then copy the <strong>gathrxr.com/m/…</strong> link. Visitors see the time in their own time zone and press <em>Join meetup</em>. People without an account sign up first and come straight back to join. For a private world, joining from the link gives that person visitor access, the same as an in-world invite.</li>
              <li><strong>Your meetups</strong>: <a href="/meetups">Meetups</a> lists the ones you host, how many people joined, and lets you copy or cancel them. A link stops working once cancelled, or a day after its start time.</li>
            </ul>

            <h3>Staying safe: mute, block and report</h3>
            <p>
              Click or tap anyone's avatar or name, use <em>People</em> in the toolbar (<em>More</em> on phones), or press
              <em> …</em> on a chat message. In VR, point at a person and pull the trigger, or open <em>People</em> beside your menu.
            </p>
            <ul className="docs-list">
              <li><strong>Mute</strong>: you stop hearing their voice and seeing their chat.</li>
              <li><strong>Block</strong>: you don't see or hear each other at all, and they can't invite you or knock on your worlds.</li>
              <li><strong>Report</strong>: tell the GathrXR team. Pick a reason, add details if you like, and we include their recent chat in that world. You can block them in the same step.</li>
            </ul>
            <p>
              Nobody is told when you mute, block or report them. Manage blocked and muted people on your Profile page.
              Visitors without an account can be muted or blocked for your current visit only.
            </p>

            <h3>Building together</h3>
            <p>
              World owners can invite others to build. Roles determine what each person can do:
              the <strong>owner</strong> controls the world, <strong>invited builders</strong> can edit,
              and <strong>visitors</strong> can explore and socialize. Edits sync live between builders.
            </p>
          </section>

          {/* ---------- Manage ---------- */}
          <section id="manage" className="docs-section">
            <h2><Gauge size={20} /> Managing your worlds</h2>
            <p>Your dashboard lists <em>My Worlds</em> and <em>Public Worlds</em>. On your own world cards you can:</p>
            <ul className="docs-list">
              <li><strong>Share</strong> — get a link to invite others.</li>
              <li><strong>Public / Private</strong> — toggle whether your world appears publicly. Private worlds use an <strong>access code</strong>: visitors enter it once (from the private world&rsquo;s link, or <em>Join Private</em> on the dashboard) and can come back without it. Too many wrong codes locks guessing for an hour.</li>
              <li><strong>Lock</strong> — block entry entirely; people can "knock" to request access, and you approve.</li>
              <li><strong>Delete</strong> — remove a world (this can't be undone).</li>
            </ul>
            <p>Inside a world, <em>World → Share Link</em> and <em>World → Multiplayer Settings</em> control invites and how many people can join.</p>
          </section>

          {/* ---------- Membership ---------- */}
          <section id="membership" className="docs-section">
            <h2><Crown size={20} /> Plans, credits & Council</h2>
            <p>Exploring, building by hand, voice, games and multiplayer are free. Paid options cover the things that cost us to generate:</p>
            <div className="docs-table-wrap">
              <table className="docs-table">
                <thead><tr><th>Option</th><th>Price</th><th>What you get</th></tr></thead>
                <tbody>
                  <tr><td>Free</td><td>$0</td><td>One free robot avatar per account, ready instantly, plus the free anime-style Avatar Builder (beta).</td></tr>
                  <tr><td>Starter</td><td>$9.99 once</td><td>Your first custom avatar, plus 4 more avatar or outfit builds and 6 recolors.</td></tr>
                  <tr><td>Studio pack</td><td>$9.99 once</td><td>6 Studio credits — about 2 worlds or 3 models, or 6 Skybox AI skies (Standard 1 credit, Enhanced 2).</td></tr>
                  <tr><td>Founding Pass</td><td>$99 once</td><td>Creator Council seat with a larger AI allowance and 60 Studio credits.</td></tr>
                  <tr><td>Creator Pro</td><td>$499 once</td><td>A bigger allowance, 150 Studio credits and creator perks.</td></tr>
                  <tr><td>Visionary Circle</td><td>$2,500 once</td><td>The top tier — limited seats and 400 Studio credits.</td></tr>
                </tbody>
              </table>
            </div>
            <p>Payments happen inside GathrXR through Stripe; your credits appear as soon as the payment goes through. Have a code? Redeem it at <a onClick={() => navigate('/redeem')} className="docs-inline-link">gathrxr.com/redeem</a>.</p>

            <h3>Creator Council</h3>
            <p>
              The <a onClick={() => navigate('/council')} className="docs-inline-link">Creator Council</a> is
              a limited founding circle — larger AI allowances, Studio credits, founding recognition, early
              access to new tools, and a voice in where GathrXR goes. Seats are capped per tier.
            </p>

            <h3>Marketplace</h3>
            <p>
              Creators can list their worlds, models, props, textures and avatars today: open{' '}
              <a onClick={() => navigate('/creator/listings')} className="docs-inline-link">Your listings</a>,
              choose what you're selling, set a price (at least $0.50), create a draft and press Publish.
              Published items show in the <a onClick={() => navigate('/marketplace')} className="docs-inline-link">Marketplace</a> right
              away. Creators keep 80% of each sale, paid out through Stripe Connect — set up payouts under
              Payouts so you're ready.
            </p>
            <p>
              <strong>Buying:</strong> press Buy on any item and pay right on the page (card, Apple Pay, Google
              Pay or Link). Copies land in your account straight away (My Uploads, your avatars or your
              worlds); buying an original transfers it to you. Creators are paid their 80% after a 7-day hold
              that protects against chargebacks; refunded or charged-back sales aren't paid out.
            </p>
          </section>

          {/* ---------- Developers ---------- */}
          <section id="developers" className="docs-section">
            <h2><Code2 size={20} /> Developers & AI assistants</h2>
            <p>
              GathrXR has an <strong>MCP server</strong>, so AI assistants such as Claude can build worlds for you:
              create a world, place shapes, models and lights, set the sky, search the model library, or generate
              new models and skyboxes — all from a chat.
            </p>
            <ul className="docs-list">
              <li><strong>claude.ai</strong> — add GathrXR as a custom connector and sign in when asked; no token needed.</li>
              <li><strong>Claude Code and other MCP clients</strong> — create an API token and paste the setup command.</li>
            </ul>
            <p>
              Ask your assistant for a world ("build a rooftop lounge at sunset with string lights and a DJ booth") and it
              appears on your dashboard, ready to edit or explore. Models and skies it brings from other sites are copied
              into GathrXR so they keep working.
            </p>
            <p>Setup steps, tokens and the full tool list are on the <a onClick={() => navigate('/developer')} className="docs-inline-link">Developer page</a>.</p>
            <Shot src="developer-mcp" alt="The Developer page with MCP connection steps" />
          </section>

          {/* ---------- Support ---------- */}
          <section id="support" className="docs-section">
            <h2><LifeBuoy size={20} /> Help & support</h2>
            <p>
              Stuck or have an idea? Ask on the <a onClick={() => navigate('/support')} className="docs-inline-link">support forum</a>.
              Search first: someone may have asked already. GathrXR Assistant, our AI moderator, answers new questions in seconds
              and brings in a person from the team when it can't help (bugs, purchases, account problems), or whenever you press
              <em> Talk to a person</em>. You get an email when the team replies. Questions about your account or payments can be
              marked <strong>private</strong> so only you and the team see them.
            </p>
            <p>
              Prefer email? Write to <a href="mailto:support@gathrxr.com" className="docs-inline-link">support@gathrxr.com</a>.
              Emails from GathrXR (receipts, guestbook notes, replies) come from hello@gathrxr.com.
              How we handle your data, and how to delete your account, is in our{' '}
              <a onClick={() => navigate('/privacy')} className="docs-inline-link">Privacy Policy</a>; the rules for
              using GathrXR and selling on the marketplace are in the{' '}
              <a onClick={() => navigate('/terms')} className="docs-inline-link">Terms of Service</a>.
              If GathrXR is useful to you, you can support it on the{' '}
              <a onClick={() => navigate('/donate')} className="docs-inline-link">donate page</a>.
            </p>
            <div className="docs-callout">
              GathrXR is in active development — features ship constantly, so if something looks
              different from these docs, it probably got better. Check back for updates.
            </div>
          </section>

          <footer className="docs-footer">
            <span>&copy; {new Date().getFullYear()} GathrXR</span>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/council')}>Creator Council</a>
              <a onClick={() => navigate('/support')}>Support</a>
              <a onClick={() => navigate('/privacy')}>Privacy</a>
              <a onClick={() => navigate('/terms')}>Terms</a>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default DocsPage;
